import * as _ from 'lodash';
import React from 'react';

class Translatable {
  /**
   * @static
   * Reference to translate function from i18n module
   * t should be referenced after i18n module is initialized, eg on index or App
   */
  static t: Function; // translation function from i18n

  /**
   * Translatable namespace separator
   * @string
   */
  static namespaceSeparator?: string = ':';

  /**
   * HTML nodes map
   */
  static readonly HTML_TAGS_MAP = {
    sBold: '<span class="tp-text-semibold">',
    eBold: '</span>',
    email: '<a href="mailto:{{sendMailTo}}">{{emailDisplayText}}</a>',
    sItalic: '<i>',
    eItalic: '</i>',
    brTag: '</br>',
    nonBrTag: '</br>',
    sFontFormat: '<span style="font-weight:inherit;color:inherit;">',
    eFontFormat: '</span>',
    copyrightSign: '&#169;',
    sSpanClass: '<span class="{{className}}">',
    eSpanClass: '</span>',
    link: '<a id="{{linkId}}" href="{{linkUrl}}" target="_blank">{{linkText}}</a>',
    sLink: '<a id="{{linkId}}" href="{{linkUrl}}" target="_blank">',
    eLink: '</a>',
    newLine: '\n'
  };

  /**
   * Check if passed text contains some node element.
   * @param value
   */
  static textContainsHtmlElement = (value: string): boolean => {
    let hasElement: boolean = false;
    let tmpDiv = document.createElement('div');
    tmpDiv.innerHTML = value;
    for (let i = 0; i < tmpDiv.childNodes.length; i++) {
      hasElement = tmpDiv.childNodes[i].nodeType === Node.ELEMENT_NODE;
      if (hasElement) {
        break;
      }
    }
    tmpDiv = null;
    return hasElement;
  };

  /**
   * Wrap everything inside a span element to properly show no-encoded content.
   * NOTE: this will allow HTML code to be rendered as it is
   * @param {string} key Translatable key
   * @param {string} translated Translatable content
   *
   * @returns {string}
   */
  static dangerouslySetInnerHTML(key: string, translated: string): Element | string {
    let value: string | any = translated;
    if (value && _.isString(value)) {
      let isElement: boolean = !!value.match(/</) && Translatable.textContainsHtmlElement(value);
      if (isElement || value.match(/&#/)) {
        value = (
          <span
            key={key}
            dangerouslySetInnerHTML={{ __html: value }}
          />
        );
      }
    }
    return value;
  }

  /**
   * Allows all HTML code to be rendered properly without loosing HTML formatting.
   * @param {string} key Translatable key
   * @param {string} translated Translatable content
   *
   * @returns {string}
   */
  static formatText(key: string, translated: string | object) {
    if (_.isObject(translated)) {
      return Object.keys(translated).reduce((newTranslated, nestedKey) => {
        let value = _.get(newTranslated, nestedKey);
        return _.set(newTranslated, nestedKey, Translatable.dangerouslySetInnerHTML(nestedKey, value));
      }, _.clone(translated));
    } else {
      return Translatable.dangerouslySetInnerHTML(key, translated);
    }
  }

  /**
   * Returns translatable namespace
   * The default value is `translation`
   *
   * @returns {string}
   */
  static getNameSpace = () => {
    let NAME_SPACE: string = 'translation';
    // <debug>
    const USE_DEVELOP_NS = process.env.STORYBOOK_USE_DEVELOP_NS;
    if (Boolean(USE_DEVELOP_NS)) {
      NAME_SPACE = 'storybook';
    }
    // </debug>
    return NAME_SPACE;
  };

  /**
   * Returns translatable content or returns translatable key if there is no content or i18n is not initialized yet.
   * The translatable content will be formatted.
   * @param {string} key translatable key
   * @param {any} options Translatable options.
   * @param {string} moduleName Component module name
   * @param {string} namespacePrefix Translatable namespace prefix
   *
   * @returns {string}
   */
  static translate(key: string, moduleName: string, namespacePrefix: string = '', options?: any): string {
    let translated = Translatable.t ? Translatable.translateByModuleName(Translatable.getNameSpace(), key, options, moduleName, namespacePrefix) : key;
    translated = Translatable.formatText(key, translated);
    return translated;
  }

  /**
   * Returns translation for input field error message. Uses HTML attribute value as translatable key, if there is no translation it will return actual error message.
   * NOTE: Generic errors retrieves messages from common namespace while custom errors from parent class namespace.
   * @param {HTMLElement} field HTML element
   * @param {string} moduleName Component module name
   * @param {string} namespacePrefix Translatable namespace prefix
   * @param options
   */
  static translateErrorMessage(field: any, moduleName: string, namespacePrefix: string = '', options?: any) {
    let translationFunc = 'translate';
    let args = [field['data-error'], moduleName, namespacePrefix, options];
    if (field['data-commonnamespace']) {
      const placeholdersArgs = !!field.placeholders ? field.placeholders : {};
      translationFunc = 'translateByNamespace';
      args = [field['data-error'], placeholdersArgs];
    }

    return field['data-error'] && Translatable[translationFunc](...args);
  }

  /**
   * Returns translation from common namespace by default.
   * Different namespace can be passed to get translation from another namespace.
   * @param {string} key translatable key
   * @param {any} options Translatable options.
   * @param {string} namespace Translatable namespace
   */
  static translateByNamespace(key: string, options = {}, namespace: string = 'common') {
    options = _.merge(options, Translatable.HTML_TAGS_MAP);
    let path = `${namespace}${Translatable.namespaceSeparator}${key}`;
    let translated = Translatable.t ? Translatable.t(path, options) : key;
    translated = Translatable.formatText(key, translated);
    return translated;
  }

  /**
   * Returns translation from provided namespace for component with provided moduleName.
   * @param {string} namespace Translatable namespace
   * @param {string} key translatable key
   * @param {any} options Translatable options.
   * @param {string} moduleName Component module name
   * @param {string} namespacePrefix Translatable namespace prefix
   */
  static translateByModuleName(namespace: string, key: string, options = {}, moduleName: string, namespacePrefix: string = ''): string | any {
    options = _.merge(options, Translatable.HTML_TAGS_MAP);
    namespacePrefix = namespacePrefix ? namespacePrefix + '.' : '';
    let path = `${namespace}${Translatable.namespaceSeparator}${namespacePrefix}${moduleName}.${key}`;
    let translated = Translatable.t(path, options);
    if (translated === `${moduleName}.${key}`) {
      translated = key;
    }
    return translated;
  }
}

export default Translatable;
